import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
});

let refreshInProgress = null;

function clearSession() {
  localStorage.removeItem("tazy_access_token");
  localStorage.removeItem("tazy_refresh_token");
  localStorage.removeItem("tazy_user");
}

async function refreshAccessToken() {
  const refresh = localStorage.getItem("tazy_refresh_token");
  if (!refresh) throw new Error("No refresh token available");

  if (!refreshInProgress) {
    refreshInProgress = axios
      .post(`${API_BASE_URL}/auth/token/refresh/`, { refresh })
      .then((res) => {
        if (!res.data?.access) throw new Error("Refresh response did not include access token");
        localStorage.setItem("tazy_access_token", res.data.access);
        return res.data.access;
      })
      .finally(() => {
        refreshInProgress = null;
      });
  }
  return refreshInProgress;
}

api.interceptors.request.use((config) => {
  const access = localStorage.getItem("tazy_access_token");
  if (access) config.headers.Authorization = `Bearer ${access}`;
  if (!(config.data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json";
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newAccess = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return api(originalRequest);
      } catch (refreshError) {
        const publicPath = ["/", "/login", "/register"];
        clearSession();
        if (!publicPath.includes(window.location.pathname)) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export const healthApi = { check: () => api.get("/health/") };

export const marketplaceApi = {
  home: (params = {}) => api.get("/public/marketplace/", { params }),
  search: (params = {}) => api.get("/public/search/", { params }),
  publicAiAssist: (payload) => api.post("/public/ai/assist/", payload),
  visualSearch: (imageFile) => {
    const formData = new FormData();
    formData.append("image", imageFile);
    return api.post("/public/visual-search/", formData);
  },
  categories: () => api.get("/public/categories/"),
  categoryDetail: (code) => api.get(`/public/categories/${code}/`),
  transporterServices: (params = {}) => api.get("/public/transporters/services/", { params }),
  transporterServiceDetail: (id) => api.get(`/public/transporters/services/${id}/`),
  productDetail: (slug) => api.get(`/public/products/${slug}/`),
  createProductEnquiry: (payload) => api.post("/enquiries/", payload),
  createDirectOrder: (slug, payload) => api.post(`/public/products/${slug}/order/`, payload),
  markReviewHelpful: (id) => api.post(`/reviews/${id}/helpful/`),
  reportReview: (id, payload) => api.post(`/reviews/${id}/report/`, payload),
};

export const authApi = {
  login: (payload) => api.post("/auth/token/", payload),
  register: (payload) => api.post("/auth/register/", payload),
  me: () => api.get("/auth/me/"),
};

export const dashboardApi = { summary: () => api.get("/dashboard/summary/") };

export const phase1Api = {
  buyers: () => api.get("/accounts/buyers/"),
  sellers: () => api.get("/accounts/sellers/"),
  businesses: () => api.get("/accounts/businesses/"),
  couriers: () => api.get("/accounts/couriers/"),
  createCourier: (payload) => api.post("/accounts/couriers/", payload),
  updateCourier: (id, payload) => api.patch(`/accounts/couriers/${id}/`, payload),
  transporterServices: () => api.get("/transporters/services/"),
  createTransporterService: (payload) => api.post("/transporters/services/", payload),
  products: () => api.get("/products/"),
  createProduct: (payload) => api.post("/products/", payload),
  updateProduct: (id, payload) => api.patch(`/products/${id}/`, payload),
  productStock: (id, payload) => api.patch(`/products/${id}/stock/`, payload),
  enquiries: () => api.get("/enquiries/"),
  enquiryDecision: (id, payload) => api.post(`/enquiries/${id}/decision/`, payload),
  createOrderFromEnquiry: (id, payload = {}) => api.post(`/enquiries/${id}/order/`, payload),
  orders: () => api.get("/orders/"),
  orderStatus: (id, payload) => api.patch(`/orders/${id}/status/`, payload),
  reviews: (params = {}) => api.get("/reviews/", { params }),
  reviewEligibility: () => api.get("/reviews/eligibility/"),
  createReview: (payload) => api.post("/reviews/", payload),
  updateReview: (id, payload) => api.patch(`/reviews/${id}/`, payload),
  respondToReview: (id, payload) => api.post(`/reviews/${id}/response/`, payload),
  markReviewHelpful: (id) => api.post(`/reviews/${id}/helpful/`),
  reportReview: (id, payload) => api.post(`/reviews/${id}/report/`, payload),
  moderateReview: (id, payload) => api.post(`/reviews/${id}/moderate/`, payload),
  deliveries: () => api.get("/deliveries/"),
  createDelivery: (payload) => api.post("/deliveries/", payload),
  deliveryStatus: (id, payload) => api.patch(`/deliveries/${id}/status/`, payload),
  branches: () => api.get("/business/branches/"),
  createBranch: (payload) => api.post("/business/branches/", payload),
  updateBranch: (id, payload) => api.patch(`/business/branches/${id}/`, payload),
  suppliers: () => api.get("/business/suppliers/"),
  createSupplier: (payload) => api.post("/business/suppliers/", payload),
  updateSupplier: (id, payload) => api.patch(`/business/suppliers/${id}/`, payload),
  goodsMovements: () => api.get("/business/goods-movements/"),
  createGoodsMovement: (payload) => api.post("/business/goods-movements/", payload),
  documents: () => api.get("/verification/documents/"),
  createDocument: (payload) => api.post("/verification/documents/", payload),
  subscriptionPlans: () => api.get("/subscriptions/plans/"),
  createSubscriptionPlan: (payload) => api.post("/subscriptions/plans/", payload),
  updateSubscriptionPlan: (id, payload) => api.patch(`/subscriptions/plans/${id}/`, payload),
  financeSummary: (params = {}) => api.get("/finance/summary/", { params }),
  aiSummary: () => api.get("/ai/summary/"),
  aiUsage: (params = {}) => api.get("/ai/usage/", { params }),
  aiAssist: (payload) => api.post("/ai/assist/", payload),
  notificationSummary: () => api.get("/notifications/summary/"),
  notifications: (params = {}) => api.get("/notifications/", { params }),
  markNotificationRead: (id) => api.post(`/notifications/${id}/read/`),
  markAllNotificationsRead: () => api.post("/notifications/mark-all-read/"),
  notificationPreferences: () => api.get("/notifications/preferences/"),
  updateNotificationPreferences: (payload) => api.patch("/notifications/preferences/", payload),
  broadcastNotification: (payload) => api.post("/notifications/broadcast/", payload),
  subscriptionEntitlement: () => api.get("/subscriptions/entitlement/"),
  financeSettings: () => api.get("/finance/settings/"),
  updateFinanceSettings: (payload) => api.patch("/finance/settings/", payload),
  initiateOrderPayment: (id, payload = {}) => api.post(`/payments/orders/${id}/initiate/`, payload),
  initiateDeliveryPayment: (id, payload = {}) => api.post(`/payments/deliveries/${id}/initiate/`, payload),
  checkPaynowPayment: (id) => api.post(`/payments/paynow/${id}/status/`),
  confirmPayment: (id, payload = {}) => api.post(`/payments/transactions/${id}/confirm/`, payload),
  settleCommission: (id, payload = {}) => api.post(`/commissions/${id}/settle/`, payload),
  subscribeToPlan: (payload) => api.post("/subscriptions/subscribe/", payload),
  subscriptionActivationCodes: () => api.get("/subscriptions/activation-codes/"),
  createSubscriptionActivationCode: (payload) => api.post("/subscriptions/activation-codes/", payload),
  redeemSubscriptionCode: (payload) => api.post("/subscriptions/redeem-code/", payload),
  adminActivateSubscription: (payload) => api.post("/subscriptions/admin-activate/", payload),
  submitSubscriptionProof: (id, proof) => {
    const form = new FormData();
    form.append("proof", proof);
    return api.post(`/subscriptions/payments/${id}/proof/`, form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  reviewSubscriptionPayment: (id, payload) => api.post(`/subscriptions/payments/${id}/review/`, payload),
  updateSubscriptionStatus: (id, payload) => api.patch(`/subscriptions/${id}/status/`, payload),
  payoutRequests: (params = {}) => api.get("/payouts/", { params }),
  createPayoutRequest: (payload) => api.post("/payouts/", payload),
  decidePayoutRequest: (id, payload) => api.post(`/payouts/${id}/decision/`, payload),
  categories: () => api.get("/categories/"),
  createCategory: (payload) => api.post("/categories/", payload),
  updateCategory: (id, payload) => api.patch(`/categories/${id}/`, payload),
  sellerWorkspace: () => api.get("/seller/workspace/"),
  businessWorkspace: () => api.get("/business/workspace/"),
  updateBusinessWorkspace: (payload) => api.patch("/business/workspace/", payload),
  updateSellerWorkspace: (payload) => api.patch("/seller/workspace/", payload),
  sellerDecision: (id, payload) => api.post(`/accounts/sellers/${id}/decision/`, payload),
  sellerRequirements: (id, payload) => api.post(`/accounts/sellers/${id}/requirements/`, payload),
  businessDecision: (id, payload) => api.post(`/accounts/businesses/${id}/decision/`, payload),
  courierDecision: (id, payload) => api.post(`/accounts/couriers/${id}/decision/`, payload),
  productDecision: (id, payload) => api.post(`/products/${id}/decision/`, payload),
  documentDecision: (id, payload) => api.post(`/verification/documents/${id}/decision/`, payload),
};

export default api;
